import { Button, Card } from '@/components/UI';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator.ts';
import { formatPercent, formatStrategyLabel, keyForRow, valueClass } from './helpers';
import { renderStageBadges } from './stageBadges';
import type { CompareRow } from './types';

interface PinnedComparisonCardProps {
    pinnedRows: CompareRow[];
    selectedStrategyId: number | null;
    togglePinnedRow: (row: CompareRow) => void;
}

const PinnedComparisonCard = ({ pinnedRows, selectedStrategyId, togglePinnedRow }: PinnedComparisonCardProps) => {
    return (
        <Card
            responsivePadding
            className="mb-6 bg-zinc-900"
            subtitle="Pinned rows stay visible while you sort or paginate."
            title="Pinned Comparison"
        >
            <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                {pinnedRows.map((row) => (
                    <div className="rounded-lg border border-zinc-800 bg-[#151515] p-4" key={keyForRow(row)}>
                        <div className="mb-3 flex items-start justify-between gap-3">
                            <div>
                                <div className="font-medium text-white">{row.experimentName}</div>
                                <div className="text-xs text-zinc-500">
                                    {row.duration} • {formatTimeHorizon(row.horizonSeconds)} • #{row.experimentId}
                                </div>
                                {selectedStrategyId === null && (
                                    <div className="text-xs text-zinc-500">{formatStrategyLabel(row.strategyId, row.strategyName)}</div>
                                )}
                                <div className="mt-2">{renderStageBadges(row)}</div>
                            </div>
                            <Button size="sm" variant="outline-dark" onClick={() => togglePinnedRow(row)}>
                                Remove
                            </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <div className="text-xs uppercase tracking-wide text-zinc-500">Avg Profit</div>
                                <div className={valueClass(row.metrics.avgProfitPercent)}>
                                    {formatPercent(row.metrics.avgProfitPercent)}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-zinc-500">Alpha</div>
                                <div className={valueClass(row.metrics.avgAlphaPercent)}>{formatPercent(row.metrics.avgAlphaPercent)}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-zinc-500">Beat SPY</div>
                                <div className={valueClass(row.metrics.winRateVsSpy)}>{formatPercent(row.metrics.winRateVsSpy, 1)}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-zinc-500">Entries</div>
                                <div className="text-zinc-100">{row.metrics.totalEntries.toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-zinc-500">Profit Factor</div>
                                <div className="text-zinc-100">
                                    {row.metrics.profitFactor === null ? '-' : row.metrics.profitFactor.toFixed(2)}
                                </div>
                            </div>
                            {row.metrics.optionAvgProfitPercent !== null && (
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-zinc-500">Option Avg</div>
                                    <div className={valueClass(row.metrics.optionAvgProfitPercent)}>
                                        {formatPercent(row.metrics.optionAvgProfitPercent)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};

export default PinnedComparisonCard;
